/**
 * Care Plans & IDT (Screen E) — REAL coded care-plan + goals beside honestly-DEFERRED IDT-team and
 * compliance-timer sections. A reusable ParticipantPicker selects a member (defaulting to the
 * care-plan exemplar). Each SkyCard section is badged from its OWN meta.provenance via ProvenanceBadge
 * (never hand-tagged):
 *   1. Care Plan (op_fact_care_plan) — CODED-ONLY: status (color + label SkyBadge) / category_code /
 *      intent / period (start · end, formatted). NO free-text title/narrative is in the projection, so
 *      none is rendered. Plans render as clear cards (~1 plan/member). Below them the coded Goals list:
 *      lifecycle_status + achievement_status (color + label) + description (coded category) + due date.
 *      Goals are the COMMON honest-empty case (only 7 exist app-wide) — an absent goals list reads
 *      "No goals recorded for this member", never a zero or an error.
 *   2. IDT Team (op_dim_provider, member-panel feed pending) — renders the honest DEFERRED/MOCK state.
 *   3. Compliance Timers (fact_assessment ABSENT — a model to BUILD, not one feed away) — the honest
 *      DEFERRED state ("Awaiting assessment model"), never a fabricated cadence/coverage/zero.
 * npi and practitioner names are provider PHI and are NEVER console.logged. Each section isolates its
 * own loading/error; an empty section reads DEFERRED/real-empty honestly, never a silent zero.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyBadgeColor,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type {
  CarePlanBundle,
  CarePlan,
  Goal,
  IdtMember,
  ComplianceTimer,
} from "@shared/contracts";
import { usePage } from "../lib/usePage";
import {
  carePlanBundleSchema,
  idtMemberListSchema,
  complianceTimerListSchema,
} from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

// --- Status color maps (H5: status by color + LABEL, never a bare color; never amber-brand) ---

/** FHIR CarePlan.status -> semantic badge color. */
function carePlanStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "revoked" || s === "entered-in-error") return "danger";
  return "subtle"; // draft / unknown
}

/** FHIR Goal.lifecycleStatus -> semantic badge color. */
function goalLifecycleColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active" || s === "accepted") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "cancelled" || s === "rejected" || s === "entered-in-error") return "danger";
  return "subtle"; // proposed / planned
}

/** FHIR Goal.achievementStatus -> semantic badge color. */
function goalAchievementColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "achieved" || s === "sustaining") return "success";
  if (s === "improving" || s === "in-progress") return "informative";
  if (s === "worsening" || s === "no-progress" || s === "not-achieved" || s === "not-attainable")
    return "warning";
  return "subtle"; // no-change
}

/** A coded status -> a color+label chip; "Not recorded" when the coded value is absent. */
function StatusBadge({ value, color }: { value: string | null; color: (v: string) => SkyBadgeColor }) {
  if (!value) return <>Not recorded</>;
  return (
    <SkyBadge color={color(value)} appearance="tint" size="small">
      {value}
    </SkyBadge>
  );
}

/** Period as "start · end" using "·" (design-law: no em-dash); ongoing/unknown stated honestly. */
function formatPeriod(start: string | null, end: string | null): string {
  if (!start && !end) return "Not recorded";
  return `${start ?? "Unknown start"} · ${end ?? "ongoing"}`;
}

const goalColumns: SkyDataGridColumn<Goal>[] = [
  { key: "description", header: "Goal (coded category)", width: "34%" },
  {
    key: "lifecycle_status",
    header: "Lifecycle",
    width: "22%",
    renderCell: (_v, row) => <StatusBadge value={row.lifecycle_status} color={goalLifecycleColor} />,
  },
  {
    key: "achievement_status",
    header: "Achievement",
    width: "24%",
    renderCell: (_v, row) => <StatusBadge value={row.achievement_status} color={goalAchievementColor} />,
  },
  { key: "due_date", header: "Due", width: "20%" },
];

const idtColumns: SkyDataGridColumn<IdtMember>[] = [
  { key: "idt_role", header: "IDT role", width: "30%" },
  { key: "primary_specialty_text", header: "Specialty", width: "40%" },
  {
    key: "last_name",
    header: "Practitioner",
    width: "30%",
    renderCell: (_v, row) => practitionerLabel(row),
  },
];

function practitionerLabel(row: IdtMember): string {
  const label = [row.last_name ?? "", row.first_name ?? ""].filter(Boolean).join(", ");
  return label || "Not recorded";
}

/** One care plan rendered as a clear card: status chip + coded category / intent / period fields. */
function CarePlanCard({ plan }: { plan: CarePlan }) {
  const fields: Array<[string, string]> = [
    ["Category (coded)", plan.category_code ?? "Not recorded"],
    ["Intent", plan.intent ?? "Not recorded"],
    ["Period", formatPeriod(plan.period_start, plan.period_end)],
  ];
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] p-4">
      <div className="flex items-center gap-2">
        <SkyText size={200}>Status</SkyText>
        <StatusBadge value={plan.status} color={carePlanStatusColor} />
      </div>
      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        {fields.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <SkyText size={200}>{k}</SkyText>
            <SkyText size={300} weight="medium">
              {v}
            </SkyText>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Care Plan section (REAL coded-only). Plans render as cards; goals render below (honest-empty). */
function CarePlanSection({ memberKey }: { memberKey: string }) {
  const { data, meta, loading, error } = usePage<CarePlanBundle>(
    `/api/v1/participants/${memberKey}/care-plan`,
    carePlanBundleSchema,
  );
  // Drift guard (matches Member360 asRows): `?? []` only catches null/undefined, so a truthy non-array
  // (a drifted 200 body) would still reach .map() and throw. Collapse ANY non-array to [] instead.
  const carePlans = Array.isArray(data?.carePlans) ? data.carePlans : [];
  const goals = Array.isArray(data?.goals) ? data.goals : [];
  return (
    <SkyCard
      header={{
        title: "Care plan & goals",
        description:
          "Coded care-plan and goals from op_fact_care_plan (coded-only: no free-text title or narrative).",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading care plan" />}
      {error && <SkyMessageBar intent="error" title="Could not load care plan" message={error} />}
      {!loading && !error && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <SkyText size={300} weight="medium">
              Care plan
            </SkyText>
            {carePlans.length === 0 ? (
              <SkyText size={300}>No care plan recorded for this member.</SkyText>
            ) : (
              <div className="flex flex-col gap-3">
                {carePlans.map((plan, i) => (
                  <CarePlanCard key={i} plan={plan} />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <SkyText size={300} weight="medium">
              Goals
            </SkyText>
            {goals.length === 0 ? (
              // Goals are the common honest-empty case (only 7 exist app-wide): an absent list is NOT
              // a zero or an error — it is "none recorded".
              <SkyText size={300}>No goals recorded for this member.</SkyText>
            ) : (
              <SkyDataGrid<Goal>
                data={goals}
                columns={goalColumns}
                getRowId={(_r, i) => String(i)}
                emptyMessage="No goals recorded for this member"
              />
            )}
          </div>
        </div>
      )}
    </SkyCard>
  );
}

/** IDT Team section (DEFERRED/MOCK). Org-level roster shown until the member-panel feed lands. */
function IdtTeamSection({ memberKey }: { memberKey: string }) {
  const { data, meta, loading, error } = usePage<IdtMember[]>(
    `/api/v1/participants/${memberKey}/idt-team`,
    idtMemberListSchema,
  );
  // Drift guard: collapse ANY non-array (including a drifted 200 body) to [] before it reaches the grid.
  const rows = Array.isArray(data) ? data : [];
  return (
    <SkyCard
      header={{
        title: "Interdisciplinary team",
        description:
          "11-discipline IDT panel. The member-panel assignment feed has not landed; the org-level roster is shown as a placeholder.",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading IDT team" />}
      {error && <SkyMessageBar intent="error" title="Could not load IDT team" message={error} />}
      {!loading && !error && (
        <div className="flex flex-col gap-3">
          <SkyMessageBar
            intent="info"
            title="Awaiting roster / provider feed"
            message="Member-level IDT panel coverage is not yet sourced. Rows shown are an org-level placeholder, not a confirmed care team, and discipline coverage must not read as complete."
          />
          <SkyDataGrid<IdtMember>
            data={rows}
            columns={idtColumns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="Awaiting roster / provider feed"
          />
        </div>
      )}
    </SkyCard>
  );
}

/** Compliance Timers section (DEFERRED — fact_assessment absent; a model to build, not a feed away). */
function ComplianceSection() {
  const { meta, loading, error } = usePage<ComplianceTimer[]>(
    "/api/v1/idt/compliance-timers",
    complianceTimerListSchema,
  );
  return (
    <SkyCard
      header={{
        title: "IDT compliance timers",
        description: "42 CFR 460 assessment cadence and due dates.",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading compliance timers" />}
      {error && (
        <SkyMessageBar intent="error" title="Could not load compliance timers" message={error} />
      )}
      {!loading && !error && (
        <SkyMessageBar
          intent="warning"
          title="Awaiting assessment model"
          message="No assessment fact model exists yet. This is a model to build (not one feed away), so cadence and due dates are deferred. An absent timer must not read as compliant or as a zero."
        />
      )}
    </SkyCard>
  );
}

export function Idt() {
  // Manual pick wins; else a ?member= deep-link (from the Member 360 "open in Care Plans & IDT" link);
  // else this section's exemplar (the care-plan-richest REAL member) so the REAL care-plan renders
  // without a manual pick. The ParticipantPicker still lets the user change member.
  const [picked, setPicked] = useState<string | null>(null);
  const [params] = useSearchParams();
  const memberParam = params.get("member");
  const validParam = memberParam && /^-?\d+$/.test(memberParam) ? memberParam : null;
  const { key: defaultKey, error: exemplarError } = useExemplarMember("care_plan");
  const key = picked ?? validParam ?? defaultKey;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "idt", label: "Care Plans & IDT" }]}
        title="Care Plans & IDT"
        description="The REAL coded care-plan and goals beside the honestly-deferred IDT team and compliance timers. Each section shows where its data came from; an empty section is awaiting data or genuinely empty, never a zero."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <div className="w-80">
          <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
        </div>
        {/* The default-member lookup failed and the user has not picked one yet: surface an honest bar
            (the picker above is the way out) instead of a silent blank screen. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}
        {key && <CarePlanSection memberKey={key} />}
        {key && <IdtTeamSection memberKey={key} />}
        <ComplianceSection />
      </section>
      <PageFooter />
    </>
  );
}
